import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
// =============================================================================
//  Copyright : 2018 semantics GmbH
//  Date      : $Date: 2026-05-25 21:18:10 +0200 (Mon, 25 May 2026) $
//  Author    : Dirk Rothe
//  Worker    : $Author: d.rothe $
//  Revision  : $Rev: 163265 $
//  Purpose   : tree list parts
// =============================================================================
// ---Third-party imports
import React from 'react';
import { Menu, Loader, Checkbox } from 'semantic-ui-react';
// ---Framework imports
import { size, get, dict, someItems, isInteger } from 'ldash';
import { Actions, Sel } from 'uix/registries';
import { funcPart } from 'uix/parts';
import log from 'uix/logging';
import { Link, AutoPortal, TableSegment, XMenu, Toolbar, DebugFeature, PortalTarget } from 'uix/components';
import { useState, useSelector, usePersistentShallow, useActions, useMemo, useEffect, useDimensions } from 'uix/hooks';
import { TreeTable, TableCaseChooser } from 'uix/db/table';
import { TypeChooser, useChosenTypes, TypeDescription } from 'uix/types/components';
import { useTreeList, useDBFieldsLoading, useCustomTreeList, useTreeNodesRaw, TreelistContext } from 'uix/db/hooks';
import { useQueryParamOverrideList } from 'uix/db/link_persist';
// ---Local imports
import { rules, NTYPE } from './rules';
import styles from './tree.css';
import { useReqTreeNode } from './hooks';
// ---Exports
const INITIAL_OPTIONS = {
    descendants: false,
    subdomains: false,
    links: false,
    showLuceneQuery: false,
    luceneCqlQuery: '',
    idList: '',
    queryMode: 'db'
};
const usePersistentOptions = ()=>usePersistentShallow('TreeListFilterOptions', INITIAL_OPTIONS, 6);
const supportedDescendants = (activeNode)=>{
    let ntype = get(activeNode, 'nodeType');
    return [
        NTYPE.VLTYPE,
        NTYPE.DOMAIN_ALL
    ].some((nt)=>ntype == nt);
};
const supportedSubdomains = (activeNode)=>{
    let ntype = get(activeNode, 'nodeType');
    return [
        NTYPE.DOMAIN,
        NTYPE.BYTYPE_TYPE,
        NTYPE.BYSTATE_STATE
    ].some((nt)=>ntype == nt);
};
const isDomain = (activeNode)=>{
    let ntype = get(activeNode, 'nodeType');
    return [
        NTYPE.DOMAIN
    ].some((nt)=>ntype == nt);
};
const DeleteRecordButton = ({ otids, mode: xmode })=>{
    let deleteTreeRecords = useActions(Actions.deleteTreeRecords);
    let [erase, setErase] = useState(false);
    let confirmation = {
        content: /*#__PURE__*/ _jsxs(_Fragment, {
            children: [
                `Delete ${otids.length} records?`,
                /*#__PURE__*/ _jsxs(DebugFeature, {
                    children: [
                        /*#__PURE__*/ _jsx("br", {}),
                        /*#__PURE__*/ _jsx(Checkbox, {
                            label: "Erase",
                            checked: erase,
                            onChange: ()=>setErase(!erase)
                        })
                    ]
                })
            ]
        })
    };
    return /*#__PURE__*/ _jsx(XMenu.Item, {
        icon: "trash",
        disabled: otids.length === 0,
        title: 'Delete',
        onClick: ()=>deleteTreeRecords({
                otids,
                erase
            }),
        confirm: confirmation,
        mode: xmode
    });
};
const CASES = {
    Freigabe: 'cols=tcaption%2Crelease.value&descendants=false&filterable=true&filtered=&groupBy=oc.releasedate&links=true&selectable=true&showFooter=true&sorted=oc.releasedate&subdomains=true&types=_metadata_',
    'OCR-Lizenzen': 'cols=oo_md.licencecount%2Coo_md.licencegothic%2Coo_md.insertts&descendants=false&filterable=true&filtered=oo_md.insertts%3A2018&groupBy=domain&links=true&showFooter=true&subdomains=true&types=_metadata_',
    'Seiten/Größe': 'cols=op.filesize%2Cop.webcachesize&descendants=false&filterable=true&filtered=&groupBy=insertts&limit=1000000&links=true&selectable=true&showFooter=true&sorted=insertts&subdomains=true&types=_page_'
};
const ListMenu = ({ selected, activeNode, withTypes, linksDisabled, descendantsDisabled })=>{
    let childButton = null;
    let [opts, setOpts] = usePersistentOptions();
    let i18n = useSelector(Sel.i18n);
    let is_domain = isDomain(activeNode);
    let isSubdomainMode = false;
    if (supportedDescendants(activeNode)) {
        childButton = /*#__PURE__*/ _jsx(XMenu.Item, {
            icon: "sitemap",
            active: opts.descendants,
            title: i18n.get(`btn_include_children`),
            onClick: ()=>setOpts({
                    descendants: !opts.descendants
                }),
            disabled: descendantsDisabled
        });
    } else if (supportedSubdomains(activeNode)) {
        isSubdomainMode = opts.subdomains;
        childButton = /*#__PURE__*/ _jsx(XMenu.Item, {
            icon: "sub_domain",
            active: opts.subdomains,
            title: i18n.get(`btn_include_childdomains`),
            onClick: ()=>setOpts({
                    subdomains: !opts.subdomains
                }),
            disabled: descendantsDisabled
        });
    }
    return /*#__PURE__*/ _jsxs(_Fragment, {
        children: [
            /*#__PURE__*/ _jsx(Toolbar.Group, {
                children: /*#__PURE__*/ _jsx(DeleteRecordButton, {
                    otids: selected
                })
            }),
            /*#__PURE__*/ _jsxs(Toolbar.Group, {
                title: "Listen Optionen",
                children: [
                    withTypes && /*#__PURE__*/ _jsx(TypeChooser, {
                        subdomains: isSubdomainMode
                    }),
                    childButton,
                    /*#__PURE__*/ _jsx(XMenu.Item, {
                        icon: "chain",
                        active: opts.links,
                        title: i18n.get(`btn_include_links`),
                        onClick: ()=>setOpts({
                                links: !opts.links
                            }),
                        disabled: linksDisabled
                    }),
                    /*#__PURE__*/ _jsxs(Toolbar.More, {
                        children: [
                            /*#__PURE__*/ _jsx(TableCaseChooser, {
                                cases: CASES
                            }),
                            is_domain && /*#__PURE__*/ _jsx(XMenu.Dropdown, {
                                options: [
                                    {
                                        value: 'db',
                                        text: 'DB'
                                    },
                                    {
                                        value: 'index',
                                        text: 'Index'
                                    },
                                    {
                                        value: 'ids',
                                        text: 'IDs'
                                    }
                                ],
                                value: opts.queryMode,
                                onSetValue: (v)=>setOpts({
                                        queryMode: v
                                    })
                            })
                        ]
                    })
                ]
            })
        ]
    });
};
const LuceneQueryMenu = ({})=>{
    let [opts, setOpts] = usePersistentOptions();
    return /*#__PURE__*/ _jsx(XMenu, {
        size: "small",
        padded: true,
        attached: true,
        children: /*#__PURE__*/ _jsx(XMenu.Input, {
            fluid: true,
            icon: "search",
            defaultValue: opts.luceneCqlQuery,
            debounce: 500,
            onValueChange: (luceneCqlQuery)=>setOpts({
                    luceneCqlQuery
                }),
            title: "CQL/Lucene query"
        })
    });
};
const useLuceneTreeList = (opts, enabled)=>{
    enabled = enabled && opts.luceneCqlQuery.length > 2;
    let { treeList, loading } = useCustomTreeList('lucene', {
        query: opts.luceneCqlQuery,
        subdomains: opts.subdomains
    }, false, enabled);
    let menu = /*#__PURE__*/ _jsx(LuceneQueryMenu, {});
    return {
        treeList,
        loading,
        menu
    };
};
const IdListMenu = ({})=>{
    let [opts, setOpts] = usePersistentOptions();
    return /*#__PURE__*/ _jsx(XMenu, {
        size: "small",
        padded: true,
        attached: true,
        children: /*#__PURE__*/ _jsx(XMenu.Input, {
            fluid: true,
            icon: "search",
            defaultValue: opts.idList,
            debounce: 500,
            onValueChange: (idList)=>setOpts({
                    idList
                }),
            title: "Ids"
        })
    });
};
const useIdTreeList = (opts, enabled)=>{
    let otids = useMemo(()=>opts.idList.split(',').map((e)=>parseInt(e)).filter((e)=>e), [
        opts.idList
    ]);
    enabled = enabled && otids.length > 0;
    let treeList = useTreeNodesRaw(enabled ? otids : [], {
        validate: true
    });
    let loading = false;
    return {
        treeList,
        loading,
        menu: /*#__PURE__*/ _jsx(IdListMenu, {})
    };
};
const useGenericTreeList = (activeNode, opts, setOpts, request, enabled)=>{
    let { descendants, subdomains, links } = opts;
    let req_dname = request.dname;
    let [typeNames] = useChosenTypes();
    if (!activeNode) {
        enabled = false;
        activeNode = {};
    }
    // on-mount: derive opts from query params
    let queryOverrideOpts = useQueryParamOverrideList(opts, setOpts);
    let typeName = typeNames.join(',') || 'metadata';
    let idContext = activeNode.idContext || '';
    let stateNames = undefined;
    // START: node-type specific rules
    let withTypeFilter = !(idContext.includes('~indices') || idContext.includes('~dev'));
    let withTypeMenu = withTypeFilter;
    if (idContext.includes('~byType~')) {
        typeName = idContext.split('~').slice(-1)[0];
    } else if (idContext.includes('~byState~') && activeNode.statusName) {
        typeName = 'metadata';
        withTypeMenu = false;
        stateNames = activeNode.statusName;
    } else if (activeNode.id?.endsWith('~indices')) {
        //~ withTypeMenu = true
        typeName = [
            'place',
            'name',
            'subject',
            'glossary'
        ];
    } else {
        if (!withTypeFilter) {
            typeName = undefined;
        }
    }
    let dname = activeNode && activeNode.dname ? activeNode.dname : req_dname;
    let params = {
        typeName,
        stateNames,
        dname,
        links
    };
    let resolveLinks = true;
    if (request.id) {
        let parentid = undefined;
        if (isInteger(request.id)) {
            parentid = request.id;
        } else if (idContext.endsWith('~all')) {
            parentid = 0;
        }
        params = {
            ...params,
            parentid
        };
    }
    // i think XOR is ok between these two
    if (supportedSubdomains(activeNode)) {
        params = {
            ...params,
            subdomains
        };
    } else if (supportedDescendants(activeNode)) {
        params = {
            ...params,
            descendants
        };
    }
    // END: node-type specific rules
    if (request.limit) {
        params = {
            ...params,
            limit: request.limit
        };
    }
    let { treeList, loading } = useTreeList('treeListMain', params, resolveLinks, enabled);
    let numsubdomains = size(useSelector(Sel.subdomains));
    numsubdomains = subdomains ? numsubdomains : null;
    return {
        treeList,
        loading,
        withTypeMenu,
        numsubdomains
    };
};
export const TreeList = funcPart({
    getGraphics: [
        'types',
        'states'
    ]
}, ({ i18n, request, ...props })=>{
    let [dimensions, ref] = useDimensions({
        delta: 10
    });
    let mountPortal = useActions('mountPortal');
    let activeNode = useSelector((root)=>root.data.tree.activeNode);
    let node = useSelector((root)=>root.data.tree.byId[activeNode?.otid]);
    if (request.classname == 'test') {
        // XXX: ugly support for /vl/test/parts/TreeList/1234
        activeNode = useReqTreeNode();
    }
    let is_domain = isDomain(activeNode);
    let [opts, setOpts] = usePersistentOptions();
    let [selected, setSelected] = useState([]);
    let bottomMenu = null;
    let linksDisabled = false;
    let descendantsDisabled = false;
    let { loading, treeList, withTypeMenu, numsubdomains } = useGenericTreeList(activeNode, opts, setOpts, request, opts.queryMode == 'db' || !is_domain);
    let is_query_mode = (qm)=>is_domain && opts.queryMode == qm;
    let luceneRes = useLuceneTreeList(opts, is_query_mode('index'));
    if (is_query_mode('index')) {
        loading = luceneRes.loading;
        treeList = luceneRes.treeList;
        bottomMenu = luceneRes.menu;
        linksDisabled = true;
        descendantsDisabled = true;
    }
    let idsRes = useIdTreeList(opts, is_query_mode('ids'));
    if (is_query_mode('ids')) {
        loading = idsRes.loading;
        treeList = idsRes.treeList;
        bottomMenu = idsRes.menu;
        withTypeMenu = false;
        linksDisabled = true;
        descendantsDisabled = true;
    }
    loading = useDBFieldsLoading(loading);
    let wide = dimensions.width > 1100;
    useEffect(()=>{
        if (!wide) {
            mountPortal({
                ListPaginationPortal: false
            });
        }
    }, [
        wide
    ]);
    if (!activeNode) {
        return /*#__PURE__*/ _jsx(Loader, {
            indeterminate: true,
            active: true
        });
    }
    let menu = /*#__PURE__*/ _jsx(ListMenu, {
        selected: selected,
        activeNode: activeNode,
        withTypes: withTypeMenu,
        linksDisabled: linksDisabled,
        descendantsDisabled: descendantsDisabled
    });
    let subheader = null;
    if (is_domain) {
        if (numsubdomains) {
            subheader = `über ${numsubdomains} Domain${numsubdomains > 1 ? 's' : ''}`;
        }
    } else if (activeNode.nodeType == NTYPE.VLTYPE) {
        subheader = /*#__PURE__*/ _jsx(TypeDescription, {
            ...node
        });
    }
    let header = /*#__PURE__*/ _jsxs("div", {
        children: [
            i18n.get(`table_header_list`),
            " ",
            wide && /*#__PURE__*/ _jsx(PortalTarget, {
                id: "ListPaginationPortal"
            })
        ]
    });
    return /*#__PURE__*/ _jsx(TableSegment, {
        header: header,
        subheader: subheader,
        icon: [
            'overview',
            {
                name: 'list',
                color: 'blue'
            }
        ],
        portal: "TreeListMenuPortal",
        menu: menu,
        toolbar: true,
        bottomMenu: bottomMenu,
        loading: loading,
        externalRef: ref,
        children: /*#__PURE__*/ _jsx(TreelistContext.Provider, {
            value: [
                opts,
                setOpts
            ],
            children: /*#__PURE__*/ _jsx(TreeTable, {
                data: treeList,
                onSelectionChange: setSelected,
                showMenu: true,
                groupHeaders: "auto",
                menuPortal: "TreeListMenuPortal",
                paginationPortal: wide ? 'ListPaginationPortal' : undefined,
                showChat: true,
                programmable: true
            })
        })
    });
});
